import {
  getAllGroupNames,
  getExitPathNode,
  getTransitEntity,
  matchTransitEntity,
} from './filePathVisitorUtils';
import {
  ChildWorkflowNode,
  GroupWorkflowNode,
  JunctionWorkflowNode,
  ModuleWorkflowNode,
  TransitWorkflowNode,
} from './workflowTree';
import { WorkflowFns, WorkflowVisitor } from './workflowVisitor';

interface CreateFilterPathVisitorProps {
  currentTransit: string;
}

export const createFilterPathVisitor = ({
  currentTransit,
}: CreateFilterPathVisitorProps) => {
  const TransitStep = (node: TransitWorkflowNode) => {
    if (node.id === currentTransit && node.next.length > 0) {
      // If the current transit is equal to this transit,
      // then only traverse the modules of this state by
      // preventing the traversal function from continuing
      // once no more modules can be found.
      const filterOnlyModulesVisitor: WorkflowVisitor = {
        TransitStep: n =>
          n.next.length === 1 && n.next[0].type === 'ModuleStep'
            ? n
            : { ...n, next: [] },
        ModuleStep: n =>
          n.next.length === 1 && n.next[0].type === 'ModuleStep'
            ? ModuleStep(n)
            : { ...n, next: [] },
      };

      return WorkflowFns.traverseDepthFirst(filterOnlyModulesVisitor)(node);
    }

    return node;
  };

  const JunctionStep = (node: JunctionWorkflowNode) => {
    if (node.id === currentTransit) {
      return { ...node, next: [] };
    }

    const nodeWithOnePath: JunctionWorkflowNode = {
      ...node,
      next: node.next.filter(matchTransitEntity(getTransitEntity(node))),
    };

    return nodeWithOnePath;
  };

  const ModuleStep = (node: ModuleWorkflowNode) => {
    if (node.meta.isExecuted === false) {
      return { ...node, next: [] };
    }
    return node;
  };

  const ChildStep = (node: ChildWorkflowNode) => {
    if (node.id === currentTransit && node.next.length > 0) {
      return { ...node, next: [] };
    }

    return node;
  };

  const GroupStep = (node: GroupWorkflowNode) => {
    const allGroupNames = getAllGroupNames(node);
    if (allGroupNames.includes(currentTransit)) {
      return {
        ...node,
        next: [],
      };
    } else {
      const exitNode = getExitPathNode(node);
      return {
        ...node,
        next: [exitNode],
      };
    }
  };

  return {
    TransitStep,
    ModuleStep,
    JunctionStep,
    GroupStep,
    ChildStep,
  };
};
